import { useAuth } from '@/features/auth/useAuth';
import { useNotifications } from '@/features/notifications/api';
import { cn } from '@/lib/cn';
import type { UserRole } from '@coreltb/shared/constants';
import { Bell, FileText, Inbox, KanbanSquare, Menu, UserPlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

const CAN_CREATE_LEAD: readonly UserRole[] = [
  'admin',
  'developer',
  'dyrektor_sprzedazy',
  'marketing',
  'handlowiec',
  'kierownik',
];
const SEES_OFFERS: readonly UserRole[] = ['admin', 'developer', 'dyrektor_sprzedazy', 'handlowiec'];

/**
 * Dolny pasek nawigacji na telefonie/tablecie (< lg). Piec rownych pozycji:
 * ikona + podpis (tez „Nowy lead” — Dawid 2026-09-30: sam „+” nie mowil, co robi),
 * aktywna pozycja: zloty kolor + kreska u gory.
 */
export function MobileTabBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { user } = useAuth();
  const role = user?.role;
  const { data } = useNotifications('todo', 20);
  const unread = data?.unreadCount ?? 0;
  const canCreate = Boolean(role && CAN_CREATE_LEAD.includes(role));
  const second =
    role && SEES_OFFERS.includes(role)
      ? { to: '/oferty', label: 'Oferty', icon: FileText }
      : { to: '/crm/koszyk', label: 'Koszyk', icon: Inbox };

  return (
    <nav
      aria-label="Nawigacja dolna"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(0,0,0,0.06)] lg:hidden"
    >
      <div className="mx-auto grid h-16 max-w-lg grid-cols-5">
        <Tab to="/crm" end label="Leady" icon={KanbanSquare} />
        <Tab to={second.to} label={second.label} icon={second.icon} />
        {canCreate ? (
          <NavLink
            to="/crm/nowy"
            className="flex flex-col items-center justify-center gap-1 text-fg"
          >
            <span className="flex h-8 w-11 items-center justify-center rounded-md bg-primary text-primary-fg shadow-sm">
              <UserPlus className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <span className="text-xs font-medium">Nowy lead</span>
          </NavLink>
        ) : (
          <span />
        )}
        <Tab to="/notyfikacje" label="Powiadom." icon={Bell} badge={unread} />
        <TabButton onClick={onOpenMenu} label="Menu" icon={Menu} />
      </div>
    </nav>
  );
}

function TabBody({
  icon: Icon,
  label,
  badge = 0,
  active,
}: {
  icon: LucideIcon;
  label: string;
  badge?: number;
  active: boolean;
}): ReactNode {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          'absolute inset-x-5 top-0 h-0.5 rounded-b-md transition-colors',
          active ? 'bg-accent' : 'bg-transparent',
        )}
      />
      <span className="relative flex h-8 items-center">
        <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 2} />
        {badge > 0 && (
          <span className="absolute -right-2.5 top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-xs font-semibold leading-none text-white">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="text-xs font-medium">{label}</span>
    </>
  );
}

const TAB_CLASS = 'relative flex flex-col items-center justify-center gap-1 transition-colors';

function Tab({
  to,
  label,
  icon,
  badge,
  end,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(TAB_CLASS, isActive ? 'text-accent' : 'text-fg-muted active:text-fg')
      }
    >
      {({ isActive }) => <TabBody icon={icon} label={label} badge={badge} active={isActive} />}
    </NavLink>
  );
}

function TabButton({
  onClick,
  label,
  icon,
}: {
  onClick: () => void;
  label: string;
  icon: LucideIcon;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(TAB_CLASS, 'text-fg-muted active:text-fg')}
    >
      <TabBody icon={icon} label={label} active={false} />
    </button>
  );
}
